import React, { useState, useEffect } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styled from 'styled-components';
import { navDelay, loaderDelay } from '@utils';
import { usePrefersReducedMotion } from '@hooks';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  align-items: flex-start;
  min-height: 100vh;
  height: 100vh;
  padding: 0;
  position: relative;
  overflow: hidden;

  @media (max-height: 700px) and (min-width: 700px), (max-width: 360px) {
    height: auto;
    padding-top: var(--nav-height);
  }

  h1 {
    margin: 0 0 30px 4px;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: clamp(var(--fz-sm), 5vw, var(--fz-md));
    font-weight: 400;

    @media (max-width: 480px) {
      margin: 0 0 20px 2px;
    }
  }

  h3 {
    margin-top: 5px;
    color: var(--slate);
    line-height: 0.9;
  }

  p {
    margin: 20px 0 0;
    max-width: 540px;
  }

  .email-link {
    ${({ theme }) => theme.mixins.bigButton};
    margin-top: 50px;
  }
`;

// Absolute layer that holds the Vanta canvas — sits behind all content
const BirdsBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
`;

// Keeps hero text above the animation
const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
`;

const Hero = () => {
  const [isMounted, setIsMounted] = useState(false);
  const [BirdsComponent, setBirdsComponent] = useState(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    const timeout = setTimeout(() => setIsMounted(true), navDelay);

    // Dynamic import — SSR-safe, loads only in the browser
    import('../VantaBirds')
      .then(mod => {
        setBirdsComponent(() => mod.default);
      })
      .catch(err => {
        console.warn('VantaBirds failed to load:', err);
      });

    return () => clearTimeout(timeout);
  }, []);

  const one = <h1>Hi, my name is</h1>;
  const two = <h2 className="big-heading">Naga Vinay Avvaru.</h2>;
  const three = <h3 className="big-heading">I build intelligent systems.</h3>;
  const four = (
    <>
      <p>
        I&apos;m an AI/ML &amp; Agentic AI specialist focused on building computer vision systems,
        autonomous agent workflows, and production-grade MLOps pipelines. Currently pursuing B.E. in
        ECE at{' '}
        <a href="https://www.stjosephs.ac.in/" target="_blank" rel="noreferrer">
          St. Joseph&apos;s Institute of Technology
        </a>
        .
      </p>
    </>
  );
  const five = (
    <a
      className="email-link"
      href="mailto:nagavinay.avvaru@gmail.com"
      target="_blank"
      rel="noreferrer">
      Get In Touch
    </a>
  );

  const items = [one, two, three, four, five];

  return (
    <StyledHeroSection>
      {/* Vanta BIRDS background — only rendered client-side after mount */}
      {isMounted && BirdsComponent && (
        <BirdsBackground>
          <BirdsComponent />
        </BirdsBackground>
      )}

      {/* Hero text content — on top of birds */}
      <HeroContent>
        {prefersReducedMotion ? (
          <>
            {items.map((item, i) => (
              <div key={i}>{item}</div>
            ))}
          </>
        ) : (
          <TransitionGroup component={null}>
            {isMounted &&
              items.map((item, i) => (
                <CSSTransition key={i} classNames="fadeup" timeout={loaderDelay}>
                  <div style={{ transitionDelay: `${i + 1}00ms` }}>{item}</div>
                </CSSTransition>
              ))}
          </TransitionGroup>
        )}
      </HeroContent>
    </StyledHeroSection>
  );
};

export default Hero;
